// ============================================================
// Almacenamiento local-first con IndexedDB
// Todo se guarda primero en local. Supabase es solo backup/sync.
// ============================================================

import { openDB, type IDBPDatabase } from 'idb'
import type { Patron, Reporte } from '@/types'

const DB_NAME = 'radcore-local'
const DB_VERSION = 2

type LocalDB = IDBPDatabase<{
  reportes: { key: string; value: ReporteLocal }
  patrones: { key: string; value: Patron[] }
  publicos: { key: string; value: { data: Patron[]; ts: number } }
  meta: { key: string; value: unknown }
  syncQueue: { key: string; value: OpPendiente }
}>

export interface ReporteLocal extends Reporte {
  _pendienteSync?: boolean
  _operacion?: 'upsert' | 'delete'
}

export interface OpPendiente {
  id: string                      // uuid de la operación
  usuarioId?: string              // dueño de la operación para evitar replay entre sesiones
  tabla: 'reports' | 'patterns' | 'voice_corrections' | 'critical_finding_events' | 'report_reliability_events'
  tipo: 'insert' | 'update' | 'upsert' | 'delete'
  datos: Record<string, unknown>
  pk?: string                     // pk para update/delete
  ts: number
  reintentos: number
  bloqueada?: boolean
  ultimo_error?: string
}

interface ReporteActivoMeta {
  reportId: string
  usuarioId: string
  organizacionId?: string | null
  actualizadoEn: string
}

let dbPromise: Promise<LocalDB> | null = null

function getDB(): Promise<LocalDB> {
  if (!dbPromise) {
    dbPromise = openDB<{
      reportes: { key: string; value: ReporteLocal }
      patrones: { key: string; value: Patron[] }
      publicos: { key: string; value: { data: Patron[]; ts: number } }
      meta: { key: string; value: unknown }
      syncQueue: { key: string; value: OpPendiente }
    }>(DB_NAME, DB_VERSION, {
      upgrade(database) {
        if (!database.objectStoreNames.contains('reportes')) database.createObjectStore('reportes')
        if (!database.objectStoreNames.contains('patrones')) database.createObjectStore('patrones')
        if (!database.objectStoreNames.contains('publicos')) database.createObjectStore('publicos')
        if (!database.objectStoreNames.contains('meta')) database.createObjectStore('meta')
        if (!database.objectStoreNames.contains('syncQueue')) database.createObjectStore('syncQueue')
      },
    })
  }
  return dbPromise
}

// ── Reportes locales ──────────────────────────────────────────

export async function guardarReporteLocal(reporte: ReporteLocal): Promise<void> {
  const db = await getDB()
  await db.put('reportes', { ...reporte, _pendienteSync: true }, reporte.id)
}

export async function obtenerReportesLocal(usuarioId: string): Promise<ReporteLocal[]> {
  const db = await getDB()
  const todos = await db.getAll('reportes')
  return todos
    .filter(r => r.autor_id === usuarioId && r._operacion !== 'delete')
    .sort((a, b) => new Date(b.creado_en).getTime() - new Date(a.creado_en).getTime())
}

export async function obtenerReporteLocal(id: string): Promise<ReporteLocal | null> {
  const db = await getDB()
  const r = await db.get('reportes', id)
  return r ?? null
}

export async function eliminarReporteLocal(id: string): Promise<void> {
  const db = await getDB()
  // Marcamos como eliminado en vez de borrar — para que el sync lo propague
  const r = await db.get('reportes', id)
  if (r) {
    await db.put('reportes', { ...r, _operacion: 'delete', _pendienteSync: true }, id)
  }
}

export async function eliminarReporteLocalDefinitivo(id: string): Promise<void> {
  const db = await getDB()
  await db.delete('reportes', id)
}

export async function reemplazarReportesLocal(usuarioId: string, reportes: Reporte[]): Promise<void> {
  const db = await getDB()
  const tx = db.transaction('reportes', 'readwrite')
  // Borrar los que no tienen sync pendiente
  const todos = await tx.store.getAll()
  for (const r of todos) {
    if (r.autor_id === usuarioId && !r._pendienteSync) {
      await tx.store.delete(r.id)
    }
  }
  // Insertar los nuevos
  for (const r of reportes) {
    await tx.store.put(r as ReporteLocal, r.id)
  }
  await tx.done
}

function claveReporteActivo(usuarioId: string, organizacionId?: string | null): string {
  return `active-report:${usuarioId}:${organizacionId ?? 'personal'}`
}

export async function guardarReporteActivoLocal(params: {
  usuarioId: string
  organizacionId?: string | null
  reportId: string
}): Promise<void> {
  if (!params.usuarioId || !params.reportId) return
  const db = await getDB()
  await db.put('meta', {
    reportId: params.reportId,
    usuarioId: params.usuarioId,
    organizacionId: params.organizacionId ?? null,
    actualizadoEn: new Date().toISOString(),
  } satisfies ReporteActivoMeta, claveReporteActivo(params.usuarioId, params.organizacionId))
}

export async function obtenerReporteActivoLocal(params: {
  usuarioId: string
  organizacionId?: string | null
}): Promise<string | null> {
  if (!params.usuarioId) return null
  const db = await getDB()
  const meta = await db.get('meta', claveReporteActivo(params.usuarioId, params.organizacionId))
  if (!meta || typeof meta !== 'object' || !('reportId' in meta)) return null
  const reportId = (meta as ReporteActivoMeta).reportId
  return typeof reportId === 'string' && reportId ? reportId : null
}

export async function limpiarReporteActivoLocal(params: {
  usuarioId: string
  organizacionId?: string | null
}): Promise<void> {
  if (!params.usuarioId) return
  const db = await getDB()
  await db.delete('meta', claveReporteActivo(params.usuarioId, params.organizacionId))
}

// ── Biblioteca pública cacheada (TTL 24h) ─────────────────────

const TTL_PUBLICOS_MS = 24 * 60 * 60 * 1000

export async function obtenerPublicosCache(): Promise<Patron[] | null> {
  const db = await getDB()
  const entry = await db.get('publicos', 'all')
  if (!entry) return null
  if (Date.now() - entry.ts > TTL_PUBLICOS_MS) return null
  return entry.data
}

export async function guardarPublicosCache(data: Patron[]): Promise<void> {
  const db = await getDB()
  await db.put('publicos', { data, ts: Date.now() }, 'all')
}

// ── Cola de sincronización ────────────────────────────────────

export async function encolar(op: Omit<OpPendiente, 'id' | 'ts' | 'reintentos'>): Promise<void> {
  const db = await getDB()
  const entry: OpPendiente = {
    ...op,
    id: crypto.randomUUID(),
    ts: Date.now(),
    reintentos: 0,
  }
  await db.put('syncQueue', entry, entry.id)
}

export async function obtenerPendientes(): Promise<OpPendiente[]> {
  const db = await getDB()
  const todas = await db.getAll('syncQueue')
  return todas
    .filter(op => !op.bloqueada)
    .sort((a, b) => a.ts - b.ts)
}

export async function eliminarPendiente(id: string): Promise<void> {
  const db = await getDB()
  await db.delete('syncQueue', id)
}

export async function incrementarReintentos(id: string): Promise<void> {
  const db = await getDB()
  const op = await db.get('syncQueue', id)
  if (op) {
    await db.put('syncQueue', { ...op, reintentos: op.reintentos + 1 }, id)
  }
}

export async function marcarPendienteFallida(id: string, error?: string): Promise<void> {
  const db = await getDB()
  const op = await db.get('syncQueue', id)
  if (op) {
    await db.put('syncQueue', { ...op, bloqueada: true, ultimo_error: error }, id)
  }
}
